/**
 * Composable para salvar e restaurar o progresso de vídeos
 * Funciona tanto para usuários logados quanto deslogados usando localStorage
 */

import { STORAGE_KEYS } from '~/constants/storageKeys';

const VIDEO_PROGRESS_KEY = STORAGE_KEYS.VIDEO_PROGRESS;
const MAX_PROGRESS_AGE = 7 * 24 * 60 * 60 * 1000; // 7 dias

interface VideoProgress {
	propertyId: string;
	currentTime: number;
	duration: number;
	timestamp: number;
}

function getVideoProgress(propertyId: string): number | null {
	if (!import.meta.client) return null;

	try {
		const stored = localStorage.getItem(VIDEO_PROGRESS_KEY);
		if (!stored) return null;

		const progressMap: Record<string, VideoProgress> = JSON.parse(stored);
		const progress = progressMap[propertyId];

		if (!progress) return null;

		// Verificar se o progresso não expirou
		if (Date.now() - progress.timestamp > MAX_PROGRESS_AGE) {
			return null;
		}

		// Retornar o tempo atual se a duração ainda for válida
		// Se a duração mudou muito (>10%), não restaurar
		return progress.currentTime;
	} catch {
		return null;
	}
}

function saveVideoProgress(
	propertyId: string,
	currentTime: number,
	duration: number
): void {
	if (!import.meta.client) return;

	try {
		const stored = localStorage.getItem(VIDEO_PROGRESS_KEY);
		const progressMap: Record<string, VideoProgress> = stored
			? JSON.parse(stored)
			: {};

		// Só salvar se o vídeo foi assistido por pelo menos 3 segundos
		if (currentTime < 3) return;

		// Não salvar se chegou ao final (permitir reiniciar)
		if (currentTime >= duration * 0.95) {
			delete progressMap[propertyId];
		} else {
			progressMap[propertyId] = {
				propertyId,
				currentTime,
				duration,
				timestamp: Date.now()
			};
		}

		// Limitar a 100 vídeos salvos
		const entries = Object.entries(progressMap);
		if (entries.length > 100) {
			// Remover os mais antigos
			const sorted = [...entries].sort(
				(a, b) => a[1].timestamp - b[1].timestamp
			);
			const toRemove = sorted.slice(0, entries.length - 100);
			toRemove.forEach(([id]) => delete progressMap[id]);
		}

		localStorage.setItem(VIDEO_PROGRESS_KEY, JSON.stringify(progressMap));
	} catch {
		// localStorage unavailable
	}
}

function clearVideoProgress(propertyId: string): void {
	if (!import.meta.client) return;

	try {
		const stored = localStorage.getItem(VIDEO_PROGRESS_KEY);
		if (!stored) return;

		const progressMap: Record<string, VideoProgress> = JSON.parse(stored);
		delete progressMap[propertyId];
		localStorage.setItem(VIDEO_PROGRESS_KEY, JSON.stringify(progressMap));
	} catch {
		// localStorage unavailable
	}
}

export function useVideoProgress() {
	return {
		getVideoProgress,
		saveVideoProgress,
		clearVideoProgress
	};
}
